// ---------------------------------------------------------------------------
// Shared "split a series' columns into small batches" logic — used by BOTH
// exportProject.ts (postMessage to exportProjectWorker.ts) and storage.ts
// (IndexedDB `put()`s), which hit the exact same class of problem for the
// exact same reason: cloning/serializing a Duero-scale project's series
// (1728 sub-basins, ~1GB across precipitation/temperature/ETP) in ONE shot
// needs a full duplicate in memory at once — "Data cannot be cloned, out of
// memory" via postMessage, or the equivalent structured-clone cost inside a
// single giant IndexedDB `put()`. Splitting into small batches keeps each
// individual clone/write small regardless of total project size.
//
// Batches split by POSITION within `series.columns`/`series.values`, NOT by
// matching `subBasin.subBasinCode` against a column NAME — a real bug found
// while building this: `engine.ts`'s `basinColumn` (and everywhere else
// that reads a sub-basin's own series) resolves a column purely by the
// INDEX stored on the sub-basin (`precipCol`/`etpCol`/`tempCol`/
// `pumpingCol`, resolved once at import/link time by
// `resolveIndicesByName`) — nothing at runtime guarantees `series.columns[i]
// === String(subBasinCode)` stays true (a v1.0 project's columns are
// 1-based index strings, not codes at all; even a v2 project's column NAME
// is only ever read again if a later paste/import needs to re-link it by
// name). Slicing by matching name against code silently dropped/misplaced
// any column whose name didn't happen to equal a real code — position-based
// chunking has no such assumption: it's lossless and order-preserving
// regardless of what the column names are, since `concatSeriesBatches`
// simply concatenates the chunks back in the same order they were split.
// ---------------------------------------------------------------------------
import type { DailySeries } from "./dailySeries.ts";
import type { SeriesBatchSlice } from "./exportProjectWorkerMessages.ts";

/**
 * Columns per batch — not tuned via a byte budget like `rocio.ts`'s own
 * `RAW_BUDGET_BYTES` (that one has to stay under a hard ceiling for a
 * SINGLE request; this only has to keep each individual `postMessage`/
 * `put()` small, and a fixed count already does that comfortably even at
 * Duero scale, ~1728/150 ≈ 12 batches).
 */
const SERIES_BATCH_SIZE = 150;

function chunk<T>(items: readonly T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
}

/**
 * Splits ONE series into position-based batches — `[]` for `null` (nothing
 * to batch). Each series type is chunked independently of the others (they
 * can have different column counts — e.g. temperature loaded for fewer
 * stations than precipitation — so batch `i` of precipitation and batch `i`
 * of temperature don't need to, and generally won't, cover the same
 * sub-basins; each series type is reassembled entirely on its own, see
 * `concatSeriesBatches`). The `.slice()` on every value is deliberate and
 * load-bearing: this returns fresh, disposable copies, never the SAME
 * `Float64Array` instances the live, in-memory project still uses
 * elsewhere — handing those to `postMessage`'s transfer list
 * (exportProject.ts) would detach them out from under the open project;
 * IndexedDB's structured clone doesn't detach, but making a fresh copy here
 * keeps both callers' code identical instead of one silently relying on a
 * behavior the other can't share.
 */
export function chunkSeriesColumns(
  series: DailySeries | null,
  batchSize: number = SERIES_BATCH_SIZE
): SeriesBatchSlice[] {
  if (!series) return [];
  const columnChunks = chunk(series.columns, batchSize);
  const valueChunks = chunk(series.values, batchSize);
  return columnChunks.map((columns, i) => ({
    columns,
    values: valueChunks[i].map((v) => v.slice()),
  }));
}
